import { Box, Button, Card, Stack, Tab, Tabs, Typography, useMediaQuery } from "@mui/material"
import { BoxImgBadge, StackInfo, StackTitleBadge, TypCategories, TypDescription, TypStatus, TypTitle } from "./style"
import { IconStatus } from "../../../icons/IconStatus"
import { UserInfo } from "../UserInfo"
import { UserProfileOverflowMenu } from "../UserProfileOverflowMenu"
import { PureLightTheme } from "../../../styles/theme"
import { StyledBadge } from "../../ui-kit"
import type { TActiveUserResponse } from "../../../api/types"
import { USER_STATUS_OPTIONS } from "../../../consts/userStatus"
import { UserAvatar } from "../../common/UserAvatar"
import { EntityImagePreviewModal } from "../../common/EntityImagePreviewModal"
import { useEntityImagePreview } from "../../common/useEntityImagePreview"
import { useEffect, useMemo, useRef, useState } from "react"
import modalEmitter from "../../ModalComponent/modalEmitter"
import { ShortDescriptionModalBody } from "../../Modals/ShortDescriptionModalBody"
import { TabsStyle } from "../../ProjectsPage/ProjectsTabs/style"
import {
    UserGrowthToolsSection,
    UserParticipatingProjectsSection,
    UserSocialOrdersSection,
    UserTeamsSection,
} from "../UserProfileActivitySections"
import { profileApi } from "../../../api/profile"

const shortDescriptionClampText = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
} as const

interface UserDescriptionProps {
    user: TActiveUserResponse
}

export const UserDescription = ({ user }: UserDescriptionProps) => {
    const isTablet = useMediaQuery(PureLightTheme.breakpoints.down('md'));
    const isMobile = useMediaQuery(PureLightTheme.breakpoints.down('sm'));
    const { data: me } = profileApi.useGetProfileQuery()
    const statusOption = USER_STATUS_OPTIONS.find((option) => String(option.value) === user.status)
    const statusLabel = statusOption?.label ?? user.status
    const fullName = user.fullname || user.shortname
    const locationPart = user.location !== null && user.location !== '' ? `${user.location}` : ''
    const competencies = user.competencies.map((item) => item.name)
    const resources = user.resources.map((item) => item.name)
    const participation = user.participation.map((item) => item.name)
    const improvements = user.improvements.map((item) => item.name)

    const shortDescription = (user.description ?? '').trim()
    const fullDescription = useMemo(
        () => (user.full_description ?? '').trim() || (user.about ?? '').trim(),
        [user.full_description, user.about],
    )
    const hasAboutSection = shortDescription.length > 0 || fullDescription.length > 0
    const hasDistinctFullDescription = Boolean(
        fullDescription.length > 0 && fullDescription !== shortDescription,
    )
    const isOwnProfile = me?.id === user.id

    const [isShortDescriptionOverflow, setIsShortDescriptionOverflow] = useState(false)
    const [currentTab, setCurrentTab] = useState(0)
    const shortDescriptionRef = useRef<HTMLSpanElement>(null)
    const canExpandDescription = isShortDescriptionOverflow || hasDistinctFullDescription
    const {
        canPreview: canPreviewAvatar,
        openPreview: openAvatarPreview,
        closePreview: closeAvatarPreview,
        galleryOpen: avatarGalleryOpen,
        galleryImages: avatarGalleryImages,
    } = useEntityImagePreview(user.avatar)

    const activityTabs = useMemo(() => {
        const tabs: Array<{ key: string; label: string }> = [{ key: 'description', label: 'Описание' }]

        if (isOwnProfile || user.show_projects_participation !== false) {
            tabs.push({ key: 'projects', label: 'Проекты' })
        }
        if (isOwnProfile || user.show_teams_participation !== false) {
            tabs.push({ key: 'teams', label: 'Команды' })
        }
        if (isOwnProfile || user.show_social_orders_participation !== false) {
            tabs.push({ key: 'social-orders', label: 'Соц-заказы' })
        }

        tabs.push({ key: 'growth-tools', label: 'Инструменты роста' })

        return tabs
    }, [
        isOwnProfile,
        user.show_projects_participation,
        user.show_teams_participation,
        user.show_social_orders_participation,
    ])

    const currentTabKey = activityTabs[currentTab]?.key ?? 'description'

    useEffect(() => {
        if (currentTab < activityTabs.length) {
            return
        }

        setCurrentTab(0)
    }, [activityTabs.length, currentTab])

    const handleAboutModalOpen = () => {
        if (!canExpandDescription) return

        modalEmitter.emitOpenModal({
            dialog: {
                title: hasDistinctFullDescription ? 'Полное описание' : 'Краткое описание',
                body: <ShortDescriptionModalBody text={fullDescription} />,
            },
            closeAtion: { visible: false },
            confirmAtion: { visible: false },
        })
    }

    useEffect(() => {
        if (currentTab !== 0) return

        const element = shortDescriptionRef.current
        if (!element) return

        const observer = new ResizeObserver(() => {
            setIsShortDescriptionOverflow(element.scrollHeight > element.clientHeight)
        })

        observer.observe(element)

        return () => observer.disconnect()
    }, [currentTab, shortDescription])
    
    return (
        <Box>
            <Stack direction="row" mb={2}>
                <Box sx={BoxImgBadge}>
                    <StyledBadge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                        invisible={!user.is_online}
                        
                    >
                       <UserAvatar
                         src={user.avatar}
                         size={150}
                         onClick={canPreviewAvatar ? openAvatarPreview : undefined}
                       />
                    </StyledBadge>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {isMobile ? (
                        <Box mb={1}>
                            <UserAvatar
                              src={user.avatar}
                              size={150}
                              onClick={canPreviewAvatar ? openAvatarPreview : undefined}
                            />
                        </Box>
                    ) : null}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1.5}
                        sx={StackTitleBadge}
                    >
                        <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="h1" sx={TypTitle}>{fullName}</Typography>
                            {isMobile && (
                                <StyledBadge
                                    invisible={!user.is_online}
                                    variant="dot"
                                    size={12}
                                />
                            )}
                        </Stack>
                        {!isOwnProfile && <UserProfileOverflowMenu userId={user.id} />}
                    </Stack>
                    {user.slogan !== null && (
                        <Typography variant="h3" sx={TypStatus}>
                            {user.slogan} <IconStatus />
                        </Typography>
                    )}
                    <Stack direction="column" gap={1.5} sx={StackInfo}>
                        <Typography variant="h4" sx={TypDescription}>
                            {user.work ? `${user.work}, ` : ''}{user.age} лет, {statusLabel}
                        </Typography>
                        <Typography variant="h4" sx={TypDescription}>{locationPart}</Typography>
                    </Stack>
                </Box>
            </Stack>
            <Stack direction="column" gap={{ xs: 1.5, sm: 2 }} mb={2} sx={{ minWidth: 0 }}>
                <Tabs
                    value={currentTab}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={TabsStyle}
                    onChange={(_event, value) => setCurrentTab(value)}
                >
                    {activityTabs.map((tab) => (
                        <Tab key={tab.key} label={tab.label} />
                    ))}
                </Tabs>
            </Stack>
            {currentTabKey === 'description' && (
                <Stack direction="column" gap={2}>
                    {isTablet && <UserInfo user={user} />}
                    {user.competencies.length > 0 && (
                        <Card variant="primaryAdaptiveCard">
                            <Typography variant="h3" sx={TypCategories}>Компетенции</Typography>
                            <Typography variant="body1">{competencies.join("; ")}</Typography>
                        </Card>
                    )}
                    {resources.length > 0 && (
                        <Card variant='primaryAdaptiveCard'>
                            <Typography variant="h3" sx={TypCategories}>Доступные ресурсы (для хорошего дела предоставлю)</Typography>
                            <Typography variant="body1">{resources.join("; ")}</Typography>
                        </Card>
                    )}
                    {participation.length > 0 && (
                        <Card variant='primaryAdaptiveCard'>
                            <Typography variant="h3" sx={TypCategories}>Условия участия (готовность включиться)</Typography>
                            <Typography variant="body1">{participation.join("; ")}</Typography>
                        </Card>
                    )}
                    {improvements.length > 0 && (
                        <Card variant='primaryAdaptiveCard'>
                            <Typography variant="h3" sx={TypCategories}>Направления развития (хочу научится)</Typography>
                            <Typography variant="body1">{improvements.join("; ")}</Typography>
                        </Card>
                    )}
                    {hasAboutSection && (
                        <Card variant='primaryAdaptiveCard'>
                            <Typography variant="h3" sx={TypCategories}>О себе</Typography>
                            <Typography
                                ref={shortDescriptionRef}
                                variant="body1"
                                sx={shortDescription ? shortDescriptionClampText : undefined}
                            >
                                {shortDescription || fullDescription}
                            </Typography>
                            {canExpandDescription && (
                                <Button variant="action" onClick={handleAboutModalOpen} sx={{ mt: 1, alignSelf: 'flex-start' }}>
                                    Полное описание
                                </Button>
                            )}
                        </Card>
                    )}
                </Stack>
            )}
            {currentTabKey === 'projects' && <UserParticipatingProjectsSection userId={user.id} />}
            {currentTabKey === 'teams' && <UserTeamsSection userId={user.id} />}
            {currentTabKey === 'social-orders' && <UserSocialOrdersSection userId={user.id} />}
            {currentTabKey === 'growth-tools' && <UserGrowthToolsSection userId={user.id} />}
            <EntityImagePreviewModal
                open={avatarGalleryOpen}
                onClose={closeAvatarPreview}
                images={avatarGalleryImages}
            />
        </Box>
    )
}
